import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import { getAcquisitionSnapshot } from "./acquisition";

const VISITOR_ID_STORAGE_KEY = "visitorId";
const DEFAULT_TRACK_ENDPOINT = API_ENDPOINTS.track;
const MAX_ENGAGEMENT_CHUNK_MS = 30 * 60 * 1000;
const MIN_ENGAGEMENT_CHUNK_MS = 1000;
const PAGE_VIEW_DEDUP_WINDOW_MS = 1500;

let lastTrackedPage = "";
let lastTrackedPageAt = 0;
let inMemoryVisitorId = "";

export const VISITOR_SUMMARY_ENDPOINT = API_ENDPOINTS.visitorSummary;

export const VISITOR_ANALYTICS_ENDPOINT = API_ENDPOINTS.visitorAnalytics;

const createVisitorId = () => {
  if (window.crypto?.randomUUID) {
    return `visitor-${window.crypto.randomUUID()}`;
  }

  return `visitor-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

export const getOrCreateVisitorId = () => {
  try {
    const storedVisitorId = localStorage.getItem(VISITOR_ID_STORAGE_KEY);

    if (storedVisitorId) {
      return storedVisitorId;
    }

    const visitorId = createVisitorId();
    localStorage.setItem(VISITOR_ID_STORAGE_KEY, visitorId);
    return visitorId;
  } catch (error) {
    if (!inMemoryVisitorId) {
      inMemoryVisitorId = createVisitorId();
    }

    return inMemoryVisitorId;
  }
};

const postTrackingPayload = async (payload, keepalive = false) => {
  if (keepalive && typeof fetch === "function") {
    try {
      const response = await fetch(DEFAULT_TRACK_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        keepalive: true,
      });

      if (!response.ok) {
        throw new Error("Tracking request failed.");
      }

      const contentType = response.headers.get("content-type") || "";
      return contentType.includes("application/json")
        ? response.json()
        : null;
    } catch (error) {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return null;
      }
    }
  }

  const response = await axios.post(DEFAULT_TRACK_ENDPOINT, payload);
  return response.data;
};

const normalizePageUrl = (pageUrl) => pageUrl || window.location.pathname;

export const trackVisitor = async (pageUrl) => {
  const normalizedPageUrl = normalizePageUrl(pageUrl);

  if (
    lastTrackedPage === normalizedPageUrl &&
    Date.now() - lastTrackedPageAt < PAGE_VIEW_DEDUP_WINDOW_MS
  ) {
    return null;
  }

  lastTrackedPage = normalizedPageUrl;
  lastTrackedPageAt = Date.now();

  const response = await postTrackingPayload({
    visitorId: getOrCreateVisitorId(),
    pageUrl: normalizedPageUrl,
    eventType: "page_view",
    occurredAt: new Date().toISOString(),
    acquisition: getAcquisitionSnapshot(normalizedPageUrl),
  });

  return response;
};

export const flushVisitorEngagement = async ({
  pageUrl,
  engagementMs,
  keepalive = false,
} = {}) => {
  const normalizedEngagementMs = Math.max(
    0,
    Math.min(MAX_ENGAGEMENT_CHUNK_MS, Math.round(Number(engagementMs) || 0))
  );

  if (normalizedEngagementMs < MIN_ENGAGEMENT_CHUNK_MS) {
    return null;
  }

  return postTrackingPayload(
    {
      visitorId: getOrCreateVisitorId(),
      pageUrl: normalizePageUrl(pageUrl),
      eventType: "engagement",
      occurredAt: new Date().toISOString(),
      engagementMs: normalizedEngagementMs,
      acquisition: getAcquisitionSnapshot(normalizePageUrl(pageUrl)),
    },
    keepalive
  );
};

export const fetchVisitorSummary = async () => {
  const response = await axios.get(VISITOR_SUMMARY_ENDPOINT);
  return response.data;
};

export const fetchVisitorAnalytics = async (
  token,
  { includeAllVisitors = false } = {}
) => {
  const response = await axios.get(VISITOR_ANALYTICS_ENDPOINT, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: includeAllVisitors ? { includeAllVisitors: "1" } : undefined,
  });

  return response.data;
};
